import React, { useState, useEffect } from 'react';
import { X, Sparkles, CheckCircle2, XCircle, Lock, Loader2, Zap as Bolt } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { fetchOfferings, purchasePackage, configureRevenueCat } from '../services/revenuecat';
import { useAuth } from '../contexts/AuthContext';
import { track, trackServer } from '../lib/analytics';
import { PROMO_ACTIVE, PROMO_BADGE, PROMO_LOCK_SHORT, PROMO_URGENCY, PRICING, ANNUAL_SAVING_PCT } from '../config/promo';

const STATIC_PLAN_META = {
    pro_200: {
        id: 'pro_200',
        name: 'BrainSync Plus',
        blurb: 'For AI summaries, transcripts and everyday saving',
        inherits: 'Basic',
        bookmarkLimit: 50,
        features: [
            'AI summaries & transcripts',
            '50 Premium AI saves / month',
            '50 Ask BrainSync / month',
            'Videos up to 30 min · 250 min/mo',
            '100 MB attachments · 2 GB storage',
        ],
        popular: true,
    },
    pro_unlimited: {
        id: 'pro_unlimited',
        name: 'BrainSync Pro',
        blurb: 'For power users with big libraries and long videos',
        inherits: 'Plus',
        bookmarkLimit: 200,
        features: [
            '200 Premium AI saves / month',
            '150 Ask BrainSync / month',
            'Videos up to 90 min · 600 min/mo',
            '500 MB attachments · 10 GB storage',
            'Priority processing',
        ],
    },
};

const FREE_PLAN = {
    id: 'free',
    name: 'BrainSync Basic',
    blurb: 'Everything you need to start your second brain',
    bookmarkLimit: 20,
    features: [
        { text: '20 Premium AI saves (lifetime)' },
        { text: 'Unlimited Basic saves' },
        { text: 'Manual categories & tags' },
        { text: 'Sharing & notes' },
        { text: 'No AI summaries / transcripts', negative: true },
    ],
};

/** Map a RevenueCat package to the plan metadata we render. */
function deriveTierFromPackage(pkg) {
    // Prefer the package identifier - it's the canonical, dashboard-configured
    // string (e.g. "bs_plus_monthly", "bs_pro_yearly", "$rc_monthly").
    const pkgId = (pkg?.identifier || '').toLowerCase();

    if (pkgId.startsWith('bs_pro_') || pkgId === 'pro_monthly' || pkgId === 'pro_yearly') return 'pro_unlimited';
    if (pkgId.startsWith('bs_plus_') || pkgId.startsWith('plus_') || pkgId === '$rc_monthly' || pkgId === '$rc_annual') return 'pro_200';

    // Fallback: peek at the attached product's identifier / display name
    const product = pkg?.product || pkg?.webBillingProduct || {};
    const productId = (product.identifier || '').toLowerCase();
    const displayName = (product.displayName || product.title || '').toLowerCase();

    if (productId.startsWith('bs_pro_')) return 'pro_unlimited';
    if (productId.startsWith('bs_plus_')) return 'pro_200';
    // Current Stripe product IDs (May 2026)
    if (['prod_usbow1km5awqlq', 'prod_usbpatpuvybp0z'].includes(productId)) return 'pro_unlimited';
    if (['prod_usbncexeh3ywsy', 'prod_usboafhdm3sgym'].includes(productId)) return 'pro_200';
    // Legacy Stripe product IDs
    if (productId === 'prod_urvoc1repjjjne') return 'pro_unlimited';
    if (productId === 'prod_urvj12tv17gipj') return 'pro_200';

    if (displayName.includes('pro')) return 'pro_unlimited';
    if (displayName.includes('plus')) return 'pro_200';

    if ((pkg?.packageType || '').toLowerCase().includes('annual')) return 'pro_unlimited';
    return 'pro_200';
}

function isYearlyPackage(pkg) {
    const id = (pkg?.identifier || pkg?.product?.identifier || '').toLowerCase();
    if (id.includes('year') || id.includes('annual')) return true;
    if ((pkg?.packageType || '').toLowerCase().includes('annual')) return true;
    return false;
}

function getPriceFromPackage(pkg) {
    // The @revenuecat/purchases-js SDK exposes pricing under several possible
    // shapes depending on version. Try them all in order of likelihood.
    if (!pkg) return null;
    const product = pkg.product || pkg.webBillingProduct || pkg.rcBillingProduct || {};

    // Web Billing v1 - currentPrice has formattedPrice + amountMicros + currency
    const cp = product.currentPrice || product.price;
    if (cp && typeof cp === 'object') {
        if (cp.formattedPrice) return cp.formattedPrice;
        if (cp.formatted) return cp.formatted;
        if (typeof cp.amountMicros === 'number' && cp.currency) {
            return `${cp.currency} ${(cp.amountMicros / 1_000_000).toFixed(2)}`;
        }
        if (typeof cp.amount === 'number' && cp.currency) {
            return `${cp.currency} ${cp.amount.toFixed(2)}`;
        }
    }

    // Older / RN-style shapes
    if (product.priceString) return product.priceString;
    if (product.price_string) return product.price_string;
    if (typeof product.price === 'number') return `$${product.price.toFixed(2)}`;
    if (typeof product.priceAmount === 'number' && product.currencyCode) {
        return `${product.currencyCode} ${product.priceAmount.toFixed(2)}`;
    }

    // Last-resort fallback: dig into the package itself
    if (pkg.priceString) return pkg.priceString;
    return null;
}

/** Promo saving vs the pre-launch list price, e.g. "$6.95" → "$4.95" = 29. */
function discountPct(was, now) {
    const w = parseFloat(String(was || '').replace(/[^0-9.]/g, ''));
    const n = parseFloat(String(now || '').replace(/[^0-9.]/g, ''));
    if (!w || !n || n >= w) return null;
    return Math.round((1 - n / w) * 100);
}

/** Split a formatted price into its currency prefix and numeric value, so we
 *  can derive a per-month figure from an annual total without assuming USD.
 *  "$49.50" → { prefix: "$", value: 49.5 } */
function splitPrice(formatted) {
    const m = String(formatted || '').match(/^(\D*)([\d.,]+)/);
    if (!m) return null;
    const value = parseFloat(m[2].replace(/,/g, ''));
    if (!isFinite(value)) return null;
    return { prefix: m[1], value };
}

/** Annual plans are advertised at their per-month equivalent, with the true
 *  annual total underneath — the same way Gamma, Notion and Figma do it.
 *  "$49.50" / year → "$4.13". */
function perMonth(formatted) {
    const p = splitPrice(formatted);
    if (!p) return null;
    return `${p.prefix}${(p.value / 12).toFixed(2)}`;
}

/** Feature lists are plain strings on paid tiers, objects on free (it has an
 *  excluded row). Normalise so one row component renders both. */
function normaliseFeature(f) {
    return typeof f === 'string' ? { text: f } : f;
}

const FeatureRow = ({ feature }) => {
    const { text, negative } = normaliseFeature(feature);
    if (negative) {
        return (
            <li className="flex items-start gap-2.5 text-[13px] text-slate-400">
                <XCircle className="w-4 h-4 flex-shrink-0 mt-[1px] text-slate-300" />
                <span className="line-through decoration-slate-300">{text}</span>
            </li>
        );
    }
    return (
        <li className="flex items-start gap-2.5 text-[13px] font-medium text-slate-700">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-[1px] text-emerald-600" />
            <span>{text}</span>
        </li>
    );
};

/** Price block. Always headlines a PER-MONTH figure — on annual billing the
 *  true yearly total sits underneath, so the comparison between the two
 *  billing modes is apples to apples. */
const PriceBlock = ({ planId, priceLabel, wasPrice, yearly }) => {
    if (!priceLabel) {
        return <p className="text-sm text-slate-500 py-1">Plan unavailable</p>;
    }
    const headline = yearly ? perMonth(priceLabel) : priceLabel;
    const wasHeadline = yearly ? perMonth(wasPrice) : wasPrice;
    const pct = PROMO_ACTIVE ? discountPct(wasHeadline, headline) : null;

    return (
        <div>
            {PROMO_ACTIVE && wasHeadline && (
                <div className="flex items-center gap-2 mb-1">
                    <span
                        className="text-sm font-semibold text-slate-400 line-through"
                        data-testid={`plan-was-price-${planId}`}
                    >
                        {wasHeadline}
                    </span>
                    {pct && (
                        <span className="text-[10.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                            {pct}% off
                        </span>
                    )}
                </div>
            )}
            <div className="flex items-baseline gap-1.5 whitespace-nowrap">
                <span className="text-[38px] font-extrabold tracking-tight text-slate-900 leading-none">
                    {headline}
                </span>
                <span className="text-sm font-medium text-slate-500">/ month</span>
            </div>
            <p className="text-[12.5px] text-slate-500 mt-1.5" data-testid={`plan-billing-note-${planId}`}>
                {yearly ? `${priceLabel} billed annually` : 'Billed monthly'}
            </p>
            {PROMO_ACTIVE && wasHeadline && (
                <p
                    className="flex items-center gap-1 mt-2 text-[11.5px] font-semibold text-emerald-700"
                    data-testid={`plan-lock-line-${planId}`}
                >
                    <Lock className="w-3 h-3" />
                    {PROMO_LOCK_SHORT}
                </p>
            )}
        </div>
    );
};

/** Urgency sits directly under the CTA, where the decision is made, rather
 *  than costing above-the-fold height at the top of the sheet. */
const UrgencyNote = ({ planId }) => (
    <p
        className="flex items-center justify-center gap-1.5 mt-2 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200/70 rounded-lg py-1.5 px-2.5 text-center"
        data-testid={`upgrade-promo-urgency-${planId}`}
    >
        <Bolt className="w-3 h-3 flex-shrink-0" />
        {PROMO_URGENCY}
    </p>
);

/** Render only ONE of the two layouts, rather than hiding one with CSS, so
 *  data-testids stay unique and automation can't match a hidden card. */
function useIsDesktop() {
    const query = '(min-width: 768px)';
    const [isDesktop, setIsDesktop] = useState(() =>
        typeof window !== 'undefined' && window.matchMedia(query).matches
    );
    useEffect(() => {
        const mql = window.matchMedia(query);
        const onChange = (e) => setIsDesktop(e.matches);
        mql.addEventListener('change', onChange);
        setIsDesktop(mql.matches);
        return () => mql.removeEventListener('change', onChange);
    }, []);
    return isDesktop;
}

export const UpgradeModal = ({ open, onClose, onPurchaseComplete, currentTier = 'free', bookmarkCount = 0, bookmarkLimit = 20, premiumUsed, premiumLimit, reason }) => {
    const [billing, setBilling] = useState('yearly');
    const [loading, setLoading] = useState(null);
    const [packages, setPackages] = useState([]);
    const [fetchingOfferings, setFetchingOfferings] = useState(false);
    const [offeringError, setOfferingError] = useState(null);
    const carouselRef = React.useRef(null);
    const carouselTouchedRef = React.useRef(false);
    const isDesktop = useIsDesktop();
    const { user } = useAuth();

    // Open the mobile carousel on the recommended plan, not on Basic. Landing
    // on the tier the user already has is the whole problem we're avoiding;
    // Basic stays one swipe to the left for anyone who wants to compare.
    // Re-runs once prices land, since card heights/widths settle then — but
    // never after the user has swiped, or we'd yank the carousel under them.
    useEffect(() => {
        if (!open || isDesktop || carouselTouchedRef.current) return;
        const el = carouselRef.current;
        if (!el) return;
        const target = el.querySelector('[data-carousel-default]');
        if (!target) return;
        const id = requestAnimationFrame(() => {
            el.scrollLeft = target.offsetLeft - el.offsetLeft;
        });
        return () => cancelAnimationFrame(id);
    }, [open, isDesktop, fetchingOfferings]);

    useEffect(() => {
        if (!open) return;
        // GA4 conversion funnel start: emit as soon as the paywall
        // becomes visible to the user (before they've picked a plan).
        // Pairs with `checkout_started` (below) → `upgrade_purchased`.
        try {
            track('paywall_shown', {
                current_tier: currentTier || 'free',
                reason: reason || undefined,
                bookmark_count: bookmarkCount || undefined,
            });
        } catch (_) { /* analytics must never break the app */ }
        let cancelled = false;
        setFetchingOfferings(true);
        setOfferingError(null);
        // Ensure RC is configured before fetching offerings - protects against
        // the race where AuthContext's initial configureRevenueCat hasn't
        // resolved yet by the time the user taps Upgrade.
        const configureAndFetch = async () => {
            if (user?.user_id) {
                await configureRevenueCat(user.user_id);
            }
            return fetchOfferings();
        };
        configureAndFetch()
            .then(pkgs => {
                if (cancelled) return;
                setPackages(pkgs);
                if (!pkgs.length) setOfferingError('No subscription plans available right now.');
            })
            .catch(err => {
                if (cancelled) return;
                setOfferingError(err?.message || 'Could not load plans');
            })
            .finally(() => !cancelled && setFetchingOfferings(false));
        return () => { cancelled = true; };
    }, [open, user?.user_id]);

    if (!open) return null;

    const handleUpgrade = async (pkg) => {
        if (!pkg) return;
        setLoading(pkg.identifier);

        // ── GA4 ── fired the moment the user commits to checkout. The
        // matching `upgrade_purchased` (with price) fires after the
        // RevenueCat callback resolves, giving us a clean funnel:
        //   upgrade_clicked → upgrade_purchased / cancelled.
        const priceUsd = Number(pkg?.webBillingProduct?.currentPrice?.amountMicros || 0) / 1e6;
        const planTier = pkg?.identifier?.startsWith('pro_') ? 'pro' :
                          pkg?.identifier?.startsWith('plus_') || pkg?.identifier?.startsWith('pro_200') ? 'plus' : 'unknown';
        track('upgrade_clicked', {
            plan_id: pkg.identifier,
            plan_tier: planTier,
            value: priceUsd || undefined,
            currency: pkg?.webBillingProduct?.currentPrice?.currency || 'USD',
            reason: reason || undefined
        });
        // Alias for Google Ads conversion tracking — GA4 lets us mark
        // `checkout_started` as a conversion for import into Ads.
        track('checkout_started', {
            plan_id: pkg.identifier,
            plan_tier: planTier,
            value: priceUsd || undefined,
            currency: pkg?.webBillingProduct?.currentPrice?.currency || 'USD',
            reason: reason || undefined
        });

        try {
            await purchasePackage(pkg);
            toast.success('Purchase complete - your plan is upgrading');
            // Server-side mirror — revenue events are the ONE thing you
            // want ad-blocker-proof, since Google Ads bidding needs the
            // conversion value to optimize. No once-per-user guard (a
            // user can legitimately upgrade multiple times over their
            // lifetime — plan-up, downgrade+re-upgrade, etc.).
            trackServer('upgrade_purchased', {
                plan_id: pkg.identifier,
                plan_tier: planTier,
                value: priceUsd || undefined,
                currency: pkg?.webBillingProduct?.currentPrice?.currency || 'USD',
                transaction_id: pkg?.identifier // RevenueCat assigns its own id post-callback; we use plan id for dedup
            });
            // Tell the parent (Dashboard) to refresh subscription state. The
            // RevenueCat webhook may take a couple of seconds to land, so we
            // poll a few times before giving up.
            onPurchaseComplete?.();
            onClose?.();
        } catch (err) {
            console.error('[UpgradeModal] purchase failed', err);
            // RC throws { code: 'USER_CANCELLED' } when the user closes the
            // checkout themselves - don't bug them with a toast.
            const cancelled = err?.code === 'USER_CANCELLED' || err?.errorCode === 'USER_CANCELLED';
            track(cancelled ? 'upgrade_cancelled' : 'upgrade_failed', {
                plan_id: pkg.identifier,
                plan_tier: planTier,
                error: err?.message || 'unknown'
            });
            if (!cancelled) {
                toast.error(err?.message || 'Could not complete the purchase');
            }
        } finally {
            setLoading(null);
        }
    };

    // Group RC packages by tier × billing interval
    const tieredPackages = {
        pro_200: { monthly: null, yearly: null },
        pro_unlimited: { monthly: null, yearly: null },
    };
    packages.forEach(pkg => {
        const tier = deriveTierFromPackage(pkg);
        const interval = isYearlyPackage(pkg) ? 'yearly' : 'monthly';
        if (tieredPackages[tier]) {
            tieredPackages[tier][interval] = pkg;
        }
    });

    const PLANS = [
        FREE_PLAN,
        { ...STATIC_PLAN_META.pro_200, packages: tieredPackages.pro_200 },
        { ...STATIC_PLAN_META.pro_unlimited, packages: tieredPackages.pro_unlimited },
    ];

    const isCurrentPlan = (planId) => currentTier === planId || (currentTier === 'free' && planId === 'free');

    const paidPlans = PLANS.filter(p => p.id !== 'free');
    const planFor = (id) => paidPlans.find(p => p.id === id) || paidPlans[0];

    /** One paid tier. Shared by the mobile carousel and the desktop grid so
     *  the two breakpoints can never drift apart. */
    const renderPaidCard = (plan, { dominant = false } = {}) => {
        const pkg = plan.packages?.[billing];
        const priceLabel = pkg ? getPriceFromPackage(pkg) : null;
        const promoTier = plan.id === 'pro_200' ? 'plus' : 'pro';
        const wasPrice = PRICING[promoTier][billing === 'yearly' ? 'yearlyWas' : 'monthlyWas'];
        const current = isCurrentPlan(plan.id);

        return (
            <div
                className={`relative flex flex-col rounded-2xl overflow-hidden ${
                    dominant
                        ? 'border-2 border-emerald-600 bg-gradient-to-b from-emerald-50/60 via-white to-white shadow-xl shadow-emerald-900/5'
                        : 'border border-slate-200 bg-white shadow-sm'
                }`}
                data-testid={`plan-card-${plan.id}`}
            >
                {dominant && (
                    <div className="flex items-center justify-center gap-1.5 py-1.5 bg-emerald-600 text-white text-[10.5px] font-bold uppercase tracking-[0.12em]">
                        <Sparkles className="w-3 h-3" />
                        Most popular
                    </div>
                )}
                <div className="flex flex-col flex-1 p-5">
                    <h3 className="text-[17px] font-bold text-slate-900">{plan.name}</h3>
                    <p className="text-[12.5px] text-slate-500 mt-1 mb-4 leading-snug">{plan.blurb}</p>

                    <PriceBlock
                        planId={plan.id}
                        priceLabel={priceLabel}
                        wasPrice={wasPrice}
                        yearly={billing === 'yearly'}
                    />

                    {/* CTA sits above the feature list, as on every modern
                        pricing page — the decision shouldn't need a scroll. */}
                    <div className="mt-5">
                        {current ? (
                            <Button variant="outline" disabled className="w-full rounded-xl" data-testid={`plan-current-${plan.id}`}>
                                Current plan
                            </Button>
                        ) : (
                            <>
                                <Button
                                    onClick={() => handleUpgrade(pkg)}
                                    disabled={!!loading || !pkg}
                                    className={`w-full rounded-xl font-semibold transition-transform active:scale-[.98] ${
                                        dominant
                                            ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm shadow-emerald-900/10'
                                            : 'bg-slate-900 text-white hover:bg-slate-800'
                                    }`}
                                    data-testid={`plan-upgrade-${plan.id}`}
                                >
                                    {loading === pkg?.identifier ? (
                                        <span className="flex items-center gap-2">
                                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecting...
                                        </span>
                                    ) : pkg ? `Upgrade to ${plan.name.replace('BrainSync ', '')}` : 'Unavailable'}
                                </Button>
                                {PROMO_ACTIVE && pkg && priceLabel && dominant && (
                                    <UrgencyNote planId={plan.id} />
                                )}
                            </>
                        )}
                    </div>

                    <p className="text-[12px] font-bold text-slate-900 mt-5 mb-2.5">
                        Everything in {plan.inherits}, and:
                    </p>
                    <ul className="space-y-2.5">
                        {plan.features.map((f, i) => <FeatureRow key={i} feature={f} />)}
                    </ul>
                </div>
            </div>
        );
    };

    /** Free tier gets a full card too, same shape as the paid ones. A
     *  one-line summary row read as an afterthought. */
    const renderFreeCard = () => (
        <div
            className="flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
            data-testid="plan-card-free"
        >
            <div className="flex flex-col flex-1 p-5">
                <h3 className="text-[17px] font-bold text-slate-900">{FREE_PLAN.name}</h3>
                <p className="text-[12.5px] text-slate-500 mt-1 mb-4 leading-snug">{FREE_PLAN.blurb}</p>
                <div className="flex items-baseline gap-1.5">
                    <span className="text-[38px] font-extrabold tracking-tight text-slate-900 leading-none">$0</span>
                    <span className="text-sm font-medium text-slate-500">/ month</span>
                </div>
                <p className="text-[12.5px] text-slate-500 mt-1.5">Free forever</p>
                <div className="mt-5">
                    {isCurrentPlan('free') ? (
                        <Button variant="outline" disabled className="w-full rounded-xl" data-testid="plan-current-free">
                            Current plan
                        </Button>
                    ) : (
                        <div className="h-10" aria-hidden />
                    )}
                </div>
                <p className="text-[12px] font-bold text-slate-900 mt-5 mb-2.5">What you get:</p>
                <ul className="space-y-2.5">
                    {FREE_PLAN.features.map((f, i) => <FeatureRow key={i} feature={f} />)}
                </ul>
            </div>
        </div>
    );

    const allCards = [
        <React.Fragment key="free">{renderFreeCard()}</React.Fragment>,
        <React.Fragment key="plus">{renderPaidCard(planFor('pro_200'), { dominant: true })}</React.Fragment>,
        <React.Fragment key="pro">{renderPaidCard(planFor('pro_unlimited'))}</React.Fragment>,
    ];

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-slate-950/60 backdrop-blur-md"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                data-testid="upgrade-modal"
            >
                <motion.div
                    className="relative w-full sm:max-w-4xl bg-white rounded-t-3xl sm:rounded-2xl border border-slate-200/80 shadow-2xl overflow-y-auto max-h-[92vh] sm:max-h-[90vh]"
                    initial={{ opacity: 0, scale: 0.96, y: 12 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ type: 'spring', damping: 26, stiffness: 300 }}
                >
                    <div className="px-5 sm:px-6 pt-5 pb-4">
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors z-10"
                            data-testid="upgrade-modal-close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        {PROMO_ACTIVE && (
                            <span
                                className="inline-flex items-center gap-1.5 mb-2.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200/80 text-emerald-800 text-[10.5px] font-bold uppercase tracking-[0.12em]"
                                data-testid="upgrade-promo-banner"
                            >
                                <Sparkles className="w-3 h-3" />
                                {PROMO_BADGE}
                                <span className="font-semibold normal-case tracking-normal text-emerald-700/90">
                                    · {PROMO_LOCK_SHORT.toLowerCase()}
                                </span>
                            </span>
                        )}
                        <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight pr-10 leading-tight">
                            {reason === 'premium_quota' ? "You're out of Premium AI saves" : 'Choose the plan that fits you'}
                        </h2>
                        <p className="text-[13px] sm:text-sm text-slate-600 mt-1 max-w-lg" data-testid="upgrade-modal-quota-line">
                            {(() => {
                                const used = premiumUsed ?? bookmarkCount;
                                const limit = premiumLimit ?? bookmarkLimit;
                                if (limit === -1) return 'Unlock more Premium AI saves + higher quotas across every capability.';
                                if (reason === 'premium_quota') {
                                    return `You've used all ${limit} Premium AI saves. New saves will be Basic (no AI summaries) until you upgrade.`;
                                }
                                return `You've used ${used} of ${limit} Premium AI saves. Upgrade for higher quotas.`;
                            })()}
                        </p>
                    </div>

                    {/* One billing control, annual first. */}
                    <div className="flex flex-col items-center gap-2 px-5 pb-2">
                        <p className="text-[12.5px] font-semibold text-emerald-700">
                            Save {ANNUAL_SAVING_PCT}% with annual billing
                        </p>
                        <div className="inline-flex items-center gap-1 p-1 rounded-full bg-slate-100 border border-slate-200/80">
                            <button
                                onClick={() => setBilling('monthly')}
                                className={`px-4 py-1.5 rounded-full text-[13px] transition-colors ${billing === 'monthly' ? 'bg-white text-slate-900 font-bold shadow-sm' : 'text-slate-600 font-medium hover:text-slate-900'}`}
                                data-testid="billing-monthly"
                            >
                                Pay monthly
                            </button>
                            <button
                                onClick={() => setBilling('yearly')}
                                className={`px-4 py-1.5 rounded-full text-[13px] transition-colors ${billing === 'yearly' ? 'bg-white text-slate-900 font-bold shadow-sm' : 'text-slate-600 font-medium hover:text-slate-900'}`}
                                data-testid="billing-yearly"
                            >
                                Pay annually
                            </button>
                        </div>
                    </div>

                    {fetchingOfferings && (
                        <div className="px-6 py-3 flex items-center justify-center gap-2 text-sm text-slate-500" data-testid="upgrade-modal-loading">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading plans...
                        </div>
                    )}
                    {offeringError && !fetchingOfferings && (
                        <div className="px-6 py-3 text-center text-sm text-red-500" data-testid="upgrade-modal-error">
                            {offeringError}
                        </div>
                    )}

                    {/* Mobile: every plan in one horizontal snap carousel, so
                        there is no plan switcher to reason about. */}
                    {!isDesktop && (
                        <div
                            ref={carouselRef}
                            onPointerDown={() => { carouselTouchedRef.current = true; }}
                            className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-5 scroll-pl-5 scroll-pr-5 pt-3 pb-1 scrollbar-none"
                            data-testid="plan-carousel"
                        >
                            {allCards.map((card, i) => (
                                <div
                                    key={i}
                                    className="snap-start shrink-0 w-[85%] max-w-[330px]"
                                    {...(i === 1 ? { 'data-carousel-default': true } : {})}
                                >
                                    {card}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Desktop: all three side by side. */}
                    {isDesktop && (
                        <div className="grid grid-cols-3 gap-5 items-start px-6 pt-4">
                            {allCards}
                        </div>
                    )}

                    <p className="text-[11px] text-slate-400 text-center px-5 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
                        Prices are subject to tax depending on your location.
                    </p>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default UpgradeModal;
